import { useTranslation } from "react-i18next";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DiscountValueType } from "../types";

interface DiscountValueSectionProps {
  valueType: DiscountValueType;
  onValueTypeChange: (type: DiscountValueType) => void;
  value: number;
  onValueChange: (value: number) => void;
  disabled?: boolean;
}

export function DiscountValueSection({
  valueType,
  onValueTypeChange,
  value,
  onValueChange,
  disabled = false,
}: DiscountValueSectionProps) {
  const { t } = useTranslation("discounts");

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseFloat(e.target.value);
    if (!Number.isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onValueChange(numValue);
    } else if (e.target.value === "") {
      onValueChange(0);
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">{t("form.value.label")}</label>
      <div className="flex gap-3 items-end">
        {/* Value Type Selector Dropdown */}
        <div className="w-[70%]">
          <Select value={valueType} onValueChange={onValueTypeChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue>
                {valueType === "fixed" ? t("form.value.fixedAmount") : t("form.value.percentage")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">{t("form.value.fixedAmount")}</SelectItem>
              <SelectItem value="percentage">{t("form.value.percentage")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Value Input */}
        {valueType === "percentage" ? (
          <div className="relative flex-1">
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={value || ""}
              onChange={handlePercentageChange}
              disabled={disabled}
              className="pr-8 text-right"
              placeholder="0"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              %
            </span>
          </div>
        ) : (
          <div className="flex-1">
            <CurrencyInput
              value={value}
              onValueChange={(v) => onValueChange(v || 0)}
              disabled={disabled}
              showSymbol={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}
